import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CalendarClock, CheckCircle2, Play, AlertTriangle, History } from "lucide-react";
import {
  PHASE_TEMPLATE,
  PHASE_STATUS_LABELS,
  daysLeft,
  phaseName,
} from "@/lib/basProcess/phases";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import { fetchPhaseBlockers, type ChecklistBlocker } from "@/lib/basProcess/phaseGate";

interface Props {
  projectId: string;
}

interface Phase {
  id: string;
  phase_key: string;
  name: string;
  sort_order: number;
  status: string;
  start_date: string | null;
  due_date: string | null;
  closure_note: string | null;
  closed_at: string | null;
}

interface Deferral {
  id: string;
  question_text: string | null;
  question_id: string;
  source_type: string;
  from_phase: string | null;
  to_phase: string | null;
  reason_code: string | null;
  reason_text: string | null;
  status: string;
  decided_by_name: string | null;
  decided_at: string;
}

export const PhaseManager: React.FC<Props> = ({ projectId }) => {
  const [phases, setPhases] = useState<Phase[]>([]);
  const [deferrals, setDeferrals] = useState<Deferral[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [extendReason, setExtendReason] = useState<Record<string, string>>({});
  const [blockers, setBlockers] = useState<ChecklistBlocker[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("project_phases")
      .select("*")
      .eq("project_id", projectId)
      .order("sort_order");

    let list = (data || []) as Phase[];
    if (list.length === 0) {
      const seed = PHASE_TEMPLATE.map((p, i) => ({
        project_id: projectId,
        phase_key: p.key,
        name: p.name,
        sort_order: i,
        status: i === 0 ? "pagaende" : "planerad",
      }));
      const { data: created } = await supabase
        .from("project_phases")
        .insert(seed)
        .select("*");
      list = ((created || []) as Phase[]).sort((a, b) => a.sort_order - b.sort_order);
    }
    setPhases(list);

    const { data: defs } = await supabase
      .from("question_deferrals")
      .select("*")
      .eq("project_id", projectId)
      .order("decided_at", { ascending: false });
    setDeferrals((defs || []) as Deferral[]);

    const activePhase = list.find((p) => p.status === "pagaende");
    setBlockers(await fetchPhaseBlockers(projectId, activePhase?.phase_key));
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const active = phases.find((p) => p.status === "pagaende");
  const openForActive = deferrals.filter(
    (d) => d.status === "deferred" && d.to_phase === active?.phase_key
  );

  const updatePhase = async (id: string, patch: Partial<Phase>) => {
    const { error } = await supabase.from("project_phases").update(patch).eq("id", id);
    if (error) {
      toast.error("Kunde inte spara skedet");
      return;
    }
    setPhases((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const activatePhase = async (phase: Phase) => {
    await Promise.all(
      phases
        .filter((p) => p.status === "pagaende" && p.id !== phase.id)
        .map((p) => supabase.from("project_phases").update({ status: "avslutad" }).eq("id", p.id))
    );
    await supabase.from("project_phases").update({ status: "pagaende" }).eq("id", phase.id);
    await supabase
      .from("projects")
      .update({ current_phase_key: phase.phase_key })
      .eq("id", projectId);
    toast.success(`${phase.name} är nu aktivt skede`);
    load();
  };

  const closePhase = async (phase: Phase) => {
    const current = await fetchPhaseBlockers(projectId, phase.phase_key);
    if (current.length > 0) {
      setBlockers(current);
      toast.error(
        `Skedet kan inte avslutas: ${current.length} obligatoriska punkter i checklistan (Dok 53) saknar OK eller N/A med motivering`
      );
      return;
    }
    const { data: auth } = await supabase.auth.getUser();
    await supabase
      .from("project_phases")
      .update({
        status: "avslutad",
        closed_at: new Date().toISOString(),
        closed_by: auth?.user?.id ?? null,
        closure_note: note.trim() || null,
      })
      .eq("id", phase.id);

    const next = phases.find((p) => p.sort_order === phase.sort_order + 1);
    if (next) {
      await supabase.from("project_phases").update({ status: "pagaende" }).eq("id", next.id);
      await supabase
        .from("projects")
        .update({ current_phase_key: next.phase_key })
        .eq("id", projectId);
    }
    setNote("");
    toast.success(next ? `Skedet avslutat – ${next.name} har börjat` : "Skedet avslutat");
    load();
  };

  const resolveDeferral = async (id: string) => {
    await supabase
      .from("question_deferrals")
      .update({ status: "resolved", resolved_at: new Date().toISOString() })
      .eq("id", id);
    toast.success("Frågan är markerad som hanterad");
    load();
  };

  /** Förläng en framflyttad fråga till ett senare skede – kräver ny motivering. */
  const extendDeferral = async (d: Deferral) => {
    const reason = (extendReason[d.id] || "").trim();
    if (reason.length < 5) {
      toast.error("Skriv en motivering till varför frågan flyttas fram igen");
      return;
    }
    const currentIndex = phases.findIndex((p) => p.phase_key === d.to_phase);
    const next = phases.find((p) => p.sort_order === (currentIndex >= 0 ? phases[currentIndex].sort_order + 1 : (active?.sort_order ?? 0) + 1));
    if (!next) {
      toast.error("Det finns inget senare skede att flytta frågan till");
      return;
    }
    const { data: auth } = await supabase.auth.getUser();
    const name =
      (auth?.user?.user_metadata?.full_name as string | undefined) ||
      auth?.user?.email ||
      "okänd";
    const history = d.reason_text ? `${d.reason_text}\n` : "";
    const { error } = await supabase
      .from("question_deferrals")
      .update({
        from_phase: d.to_phase,
        to_phase: next.phase_key,
        reason_text: `${history}[${new Date().toLocaleDateString("sv-SE")} – förlängd av ${name}] ${reason}`,
        decided_by: auth?.user?.id ?? null,
        decided_by_name: name,
        decided_at: new Date().toISOString(),
      })
      .eq("id", d.id);
    if (error) {
      toast.error("Kunde inte förlänga frågan");
      return;
    }
    setExtendReason((prev) => ({ ...prev, [d.id]: "" }));
    toast.success(`Frågan är förlängd till ${next.name} med motivering`);
    load();
  };

  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">Laddar skeden...</p>;
  }

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="03"
        title="Skeden och skedesväxling"
        description="Sätt slutdatum för varje skede, se vad som återstår och flytta fram frågor med spårbart beslut."
        icon={CalendarClock}
      />

      {active && (
        <Card className="border-primary/40">
          <CardHeader className="pb-2">
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              Aktivt skede: {active.name}
              {active.due_date && (
                <Badge
                  variant={(daysLeft(active.due_date) ?? 0) < 0 ? "destructive" : "secondary"}
                >
                  {(daysLeft(active.due_date) ?? 0) < 0
                    ? `${Math.abs(daysLeft(active.due_date) ?? 0)} dagar försenat`
                    : `${daysLeft(active.due_date)} dagar kvar`}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {PHASE_TEMPLATE.find((p) => p.key === active.phase_key)?.description}
            </p>
            {blockers.length > 0 && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  Obligatoriska punkter i byggherrens checklista (Dok 53) blockerar skedesväxlingen
                </p>
                <ul className="mt-2 list-disc pl-6 text-sm text-muted-foreground">
                  {blockers.map((b) => (
                    <li key={b.id}>
                      {b.activity} – {b.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {openForActive.length > 0 ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  {openForActive.length} framflyttade frågor ska hanteras i detta skede – skedet
                  kan inte avslutas förrän varje fråga är hanterad eller förlängd med ny motivering
                </p>
              </div>
            ) : (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                Inga framflyttade frågor väntar i detta skede.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Skeden och datum</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="divide-y">
            {phases.map((p) => {
              const left = daysLeft(p.due_date);
              return (
                <li key={p.id} className="flex flex-wrap items-end gap-3 py-3">
                  <div className="min-w-0 flex-1 sm:min-w-[180px]">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{p.name}</span>
                      <Badge variant={p.status === "pagaende" ? "default" : "outline"}>
                        {PHASE_STATUS_LABELS[p.status] ?? p.status}
                      </Badge>
                      {p.status !== "avslutad" && left !== null && left < 0 && (
                        <Badge variant="destructive">Passerat slutdatum</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {PHASE_TEMPLATE.find((t) => t.key === p.phase_key)?.description}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Startdatum</Label>
                    <Input
                      type="date"
                      className="h-9 w-40"
                      defaultValue={p.start_date ?? ""}
                      onBlur={(e) => updatePhase(p.id, { start_date: e.target.value || null })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Handlingar klara senast</Label>
                    <Input
                      type="date"
                      className="h-9 w-40"
                      defaultValue={p.due_date ?? ""}
                      onBlur={(e) => updatePhase(p.id, { due_date: e.target.value || null })}
                    />
                  </div>
                  <div className="flex gap-2">
                    {p.status !== "pagaende" && (
                      <Button size="sm" variant="outline" onClick={() => activatePhase(p)}>
                        <Play className="mr-1 h-4 w-4" /> Gör aktivt
                      </Button>
                    )}
                    {p.status === "pagaende" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm">
                            <CheckCircle2 className="mr-1 h-4 w-4" /> Avsluta skede
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Avsluta {p.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {openForActive.length > 0
                                ? `${openForActive.length} framflyttade frågor är fortfarande öppna i detta skede. Hantera dem eller flytta fram dem igen innan du avslutar.`
                                : blockers.length > 0
                                  ? `${blockers.length} obligatoriska punkter i byggherrens checklista (Dok 53) måste vara OK eller N/A med motivering innan skedet kan avslutas.`
                                  : "Nästa skede startar automatiskt när du avslutar detta."}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <Textarea
                            rows={3}
                            placeholder="Kommentar vid avslut (valfritt)"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                          />
                          <AlertDialogFooter>
                            <AlertDialogCancel>Avbryt</AlertDialogCancel>
                            <AlertDialogAction
                              disabled={
                                openForActive.length > 0 ||
                                (p.status === "pagaende" && blockers.length > 0)
                              }
                              onClick={() => closePhase(p)}
                            >
                              {openForActive.length > 0
                                ? "Spärrat – öppna frågor"
                                : blockers.length > 0
                                  ? "Spärrat – checklista Dok 53"
                                  : "Avsluta skedet"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            <History className="h-4 w-4" /> Framflyttade frågor
          </CardTitle>
        </CardHeader>
        <CardContent>
          {deferrals.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Inga frågor har flyttats fram i projektet.
            </p>
          ) : (
            <ul className="divide-y">
              {deferrals.map((d) => (
                <li key={d.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                  <div className="min-w-0 space-y-1">
                    <p className="text-sm font-medium">
                      {d.question_text || d.question_id}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {phaseName(d.from_phase)} → {phaseName(d.to_phase)} · beslut av{" "}
                      {d.decided_by_name || "okänd"} den{" "}
                      {new Date(d.decided_at).toLocaleDateString("sv-SE")}
                    </p>
                    {d.reason_text && (
                      <p className="text-xs text-muted-foreground">Motivering: {d.reason_text}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={d.status === "resolved" ? "secondary" : "outline"}>
                        {d.status === "resolved" ? "Hanterad" : "Öppen"}
                      </Badge>
                      {d.status !== "resolved" && (
                        <Button size="sm" variant="outline" onClick={() => resolveDeferral(d.id)}>
                          Markera hanterad
                        </Button>
                      )}
                    </div>
                    {d.status !== "resolved" && (
                      <div className="flex w-full max-w-sm flex-col gap-2 sm:flex-row">
                        <Input
                          className="h-9"
                          placeholder="Motivering för att flytta fram igen"
                          value={extendReason[d.id] || ""}
                          onChange={(e) =>
                            setExtendReason((prev) => ({ ...prev, [d.id]: e.target.value }))
                          }
                        />
                        <Button size="sm" variant="secondary" onClick={() => extendDeferral(d)}>
                          Förläng
                        </Button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PhaseManager;
